import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

import { ApiError, api, unwrap } from '../api/client.js';
import { BasicAuthFields } from '../components/BasicAuthForm.js';
import { Button, Checkbox, LinkButton, Notice, TextField } from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { useSites } from '../sites.js';
import { PENDING_SITE_KEY } from './Auth.js';

/** "praxis-sommer.de" for "https://www.praxis-sommer.de/team", to find a site by its address. */
function hostKey(address: string) {
  const trimmed = address.trim().toLowerCase();
  try {
    const url = new URL(/^[a-z][a-z0-9+.-]*:\/\//.test(trimmed) ? trimmed : `https://${trimmed}`);
    return url.hostname.replace(/^www\./, '');
  } catch {
    return trimmed;
  }
}

/**
 * Website hinzufügen (design/pages/app/add-site.html, US-02): enter an address, we normalise and
 * add it, then follow its first checks. The plan's limit counts every site, paused ones too
 * (audit, decision 1).
 */
export function AddSite() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const client = useQueryClient();
  const billing = useQuery({ queryKey: ['billing'], queryFn: () => unwrap(api.GET('/billing')) });
  const sites = useSites();
  // Prefilled after registering from a Quick-Check (Register stores the address).
  const [url, setUrl] = useState(() => {
    try {
      return localStorage.getItem(PENDING_SITE_KEY) ?? '';
    } catch {
      return '';
    }
  });
  const [name, setName] = useState('');
  const [protectedSite, setProtectedSite] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [required, setRequired] = useState<string>();
  const urlField = useRef<HTMLInputElement>(null);
  const add = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/sites', {
          body: {
            url: url.trim(),
            ...(name.trim() ? { name: name.trim() } : {}),
            ...(protectedSite && username.trim() && password
              ? { basicAuth: { username: username.trim(), password } }
              : {}),
          },
        }),
      ),
    onSuccess: async (site) => {
      try {
        localStorage.removeItem(PENDING_SITE_KEY);
      } catch {
        // nothing stored
      }
      await client.invalidateQueries({ queryKey: ['sites'] });
      void navigate(`/sites/${site.id}/onboarding`, {
        state: { unreachable: !site.reachability.reachable },
      });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!url.trim()) return setRequired(t('errors.required'));
    setRequired(undefined);
    add.mutate();
  };

  const usage = billing.data?.usage;
  const plan = billing.data ? t(`admin.plans.${billing.data.planCode}`) : '';
  const error = add.error instanceof ApiError ? add.error : null;
  // The limit as the API reports it on sending, or already known from the plan.
  const limit =
    error?.code === 'PLAN_LIMIT_REACHED'
      ? (error.problem.limit?.max ?? usage?.maxSites ?? 1)
      : usage && usage.sites >= usage.maxSites
        ? usage.maxSites
        : null;
  const duplicate = error?.code === 'CONFLICT';
  const existing = duplicate
    ? sites.data?.items.find((s) => hostKey(s.host) === hostKey(url))
    : undefined;
  const fieldError =
    required ??
    (duplicate
      ? t('addSite.exists')
      : error && error.code !== 'PLAN_LIMIT_REACHED' && error.problem.status < 500
        ? errorMessage(error, t)
        : undefined);
  const failure =
    add.error && !fieldError && limit === null ? errorMessage(add.error, t) : undefined;
  const busy = add.isPending;
  // The field is disabled while sending: back into it, so the error under it is read out.
  useEffect(() => {
    if (fieldError && !busy) urlField.current?.focus();
  }, [fieldError, busy]);

  return (
    <div className="page">
      <div className="page-title">
        <div>
          <Link className="back" to="/">
            {t('addSite.back')}
          </Link>
          <h1>{t('addSite.title')}</h1>
        </div>
      </div>
      <div className="narrow">
        <p className="bodyM">{t('addSite.intro')}</p>
        <form onSubmit={submit} noValidate>
          {limit !== null ? (
            <Notice
              kind="error"
              title={t('addSite.limitTitle', { count: limit })}
              action={
                <LinkButton to="/billing" variant="secondary" size="sm">
                  {t('addSite.plans')}
                </LinkButton>
              }
            >
              {t('addSite.limitText')}
            </Notice>
          ) : null}
          {failure ? <Notice kind="error">{failure}</Notice> : null}
          <TextField
            label={t('addSite.url')}
            placeholder={t('addSite.urlPlaceholder')}
            hint={t('addSite.urlHint')}
            inputMode="url"
            autoComplete="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            error={fieldError}
            disabled={busy}
            required
            ref={urlField}
          />
          {limit === null ? (
            <>
              <TextField
                label={t('addSite.name')}
                optional
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={busy}
              />
              <Checkbox
                label={t('basicAuth.addToggle')}
                checked={protectedSite}
                onChange={(e) => setProtectedSite(e.target.checked)}
                disabled={busy}
              />
              {protectedSite ? (
                <div className="ba">
                  <b className="h4s">{t('basicAuth.title')}</b>
                  <ul>
                    <li>{t('basicAuth.add1')}</li>
                    <li>{t('basicAuth.add2')}</li>
                    <li>{t('basicAuth.add3')}</li>
                    <li>{t('basicAuth.add4')}</li>
                  </ul>
                  <BasicAuthFields
                    username={username}
                    password={password}
                    onUsername={setUsername}
                    onPassword={setPassword}
                  />
                </div>
              ) : null}
            </>
          ) : null}
          <div className="acts">
            <Button type="submit" busy={busy} disabled={limit !== null}>
              {busy ? t('addSite.checking') : t('addSite.submit')}
            </Button>
            {busy ? null : existing ? (
              <LinkButton to={`/sites/${existing.id}`} variant="secondary">
                {t('addSite.open')}
              </LinkButton>
            ) : (
              <LinkButton to="/" variant="ghost">
                {t('addSite.cancel')}
              </LinkButton>
            )}
            {usage && limit === null && !busy ? (
              <span className="hint">
                {t('addSite.limitHint', { plan, count: usage.maxSites, sites: usage.sites })}
              </span>
            ) : null}
          </div>
        </form>
      </div>
    </div>
  );
}
